/**
 * The **talent-locked affordance** the equip-pick leaves (`<WeaponSlotEditor>`/
 * `<SystemEditor>`) render — a thin display layer over the SAME single-source
 * helpers the reducer's talent-gear equip gate uses (`meetsTalentRequirement` /
 * `talentRequirement` in `@lancer-vtt/domain`).
 * So the grey-out can never drift from the wall: the reducer still hard-rejects
 * equipping talent-exclusive gear (Ammo Case I/II/III, the Technophile NHPs, the
 * Prototype Weapons, the Fuel Rod Gun) above the pilot's earned talent rank; this
 * just makes the player see *why* before clicking — mirrors `licenseLock` exactly,
 * folded in beside it in `entryFor`.
 *
 * `talentLock(pilot, item, compendium)` returns `null` when the pilot meets the
 * requirement (the item equips freely, or carries no `talent_id` at all) or a
 * `{ label }` describing the unmet rank when it doesn't — e.g.
 * `"Requires Walking Armory 1"`. Unlike `licenseLock` (whose `LicensedItem` already
 * carries a human `license` display string), a talent-gated item only carries the
 * raw `talent_id` (e.g. `"t_walking_armory"`), so the label resolves the talent's
 * display *name* off the compendium — the same `getById("talent", id)` lookup
 * `pilotBuildView` already uses to name owned talents.
 */
import {
  integratingFrame,
  isIntegratedTalentItem,
  meetsTalentRequirement,
  talentRequirement,
  type PilotData,
  type TalentGatedItem,
} from "@lancer-vtt/domain";
import type { Compendium } from "@lancer-vtt/compendium";

const ROMAN = ["", "I", "II", "III", "IV", "V", "VI"];

/** Resolve a talent id to its display name via the compendium; falls back to the raw id. */
function talentDisplayName(talentId: string, compendium: Compendium): string {
  const talent = compendium.getById("talent", talentId) as { name?: string } | undefined;
  return talent?.name ?? talentId;
}

/**
 * `null` when the pilot may equip the item; otherwise the lock with a requirement
 * label the picker shows on the greyed, disabled option. Mirrors
 * `meetsTalentRequirement` exactly — same predicate the reducer's equip rules
 * call — so a locked pick the leaf greys is precisely the pick the reducer would
 * hard-reject.
 */
export function talentLock(
  pilot: PilotData,
  item: TalentGatedItem,
  compendium: Compendium,
): { label: string } | null {
  if (meetsTalentRequirement(pilot, item)) return null;
  const req = talentRequirement(item);
  // `meetsTalentRequirement` is false ⇒ there IS a requirement, but guard anyway.
  if (!req) return null;
  const name = talentDisplayName(req.id, compendium);
  return { label: `Requires ${name} ${ROMAN[req.rank] ?? req.rank}` };
}

/**
 * `null` unless `item` is talent-**integrated** — i.e. it's on the talent's
 * `integrated[]` list, not `special_equipment` — in which case it can NEVER be
 * manually equipped, at any rank: it auto-arms at spawn
 * instead (`talentIntegratedGrants`). Mirrors `isIntegratedTalentItem` exactly (the
 * SAME predicate `applyEquipWeapon`/`applyEquipSystem` hard-reject on), so a picker
 * row this greys out is precisely a pick the reducer would bounce. Unlike
 * {@link talentLock}, this doesn't depend on the pilot at all — an integrated item
 * stays locked for manual equip whether or not the pilot holds (or has surpassed)
 * the granting rank.
 */
export function integratedLock(
  item: TalentGatedItem & { id: string },
  compendium: Compendium,
): { label: string } | null {
  if (!isIntegratedTalentItem(item, compendium)) return null;
  const req = talentRequirement(item);
  const name = req ? talentDisplayName(req.id, compendium) : "a talent";
  return { label: `Auto-granted by ${name} — not manually equippable` };
}

/**
 * `null` unless `itemId` is **frame**-integrated — hard-mounted by some chassis's
 * `integrated[]`/`core_system.integrated[]` list (the Sherman's ZF4 SOLIDCORE, the
 * Barbarossa's Apocalypse Rail, the Lancaster's Latch Drone, the Raleigh's M35
 * Mjolnir) — in which case it can NEVER be manually equipped, on any frame: it
 * auto-arms at spawn instead (`@lancer-vtt/rules`'s `spawn.ts`, in its own
 * `integrated:` key namespace outside `base.mounts`).
 *
 * The frame-side twin of {@link integratedLock}, mirroring `isFrameIntegratedItem`
 * exactly — the SAME predicate `applyEquipWeapon`/`applyEquipSystem` hard-reject on.
 * These items carry no `source`, no `license` and no `talent_id`, so **no other gate
 * in the picker's chain catches them** — without this lock they show up as freely
 * addable Main weapons on any chassis.
 */
export function frameIntegratedLock(itemId: string, compendium: Compendium): { label: string } | null {
  const frame = integratingFrame(itemId, compendium);
  if (!frame) return null;
  return { label: `Integrated into the ${frame.name ?? frame.id} — not manually equippable` };
}
